import { Injectable } from '@angular/core';
import {
	GridBoardGameData,
	RendererGameDefinitionService,
	GridItem,
	PluginInterfaceId,
} from '@meta-press/shared-plugins';
import { PluginType } from '@meta-press/shared-plugins';
import { GameProductionState } from '../model/GameProductionState';
import { GameTag, GameCategory } from '../model/GameClassification';
import {
	EditorGameDefinition,
	EditorGameDefinitionPreset,
} from '../EditorGameDefinition';
import {
	MultiChoiceGameData,
	GameData,
	InteractiveReadingExperienceGameData,
	SectionContentType,
	DesignElementType,
	MindMapGameData,
	PluginWordMergeGameData,
	SequenceOrderingGameData,
	ArgumentationGameData,
	PredictionTriviaGameData,
	TriviaParagraph,
	StudyMaterialSummaryGameData,
	StudyMaterialSummaryEditorTopic,
	StudyMaterialSummaryEditorParagraph,
	PluginDetectiveCaseStudyGameData,
	PluginDetectiveGameData,
	CardSortingGameData,
	PluginDigestGameData,
	PluginCanvasGameData,
	PluginSliderGameData,
	PluginTextMessageConversationGameData,
	CrosswordPuzzleGameData,
	PluginFAQGameData,
	PluginWordGuessGameData,
	HintMode,
	PluginMiniCryptoGameData,
	PluginWordMazeGameData,
	TextRendererGameData,
} from '@meta-press/shared-plugins';
import { WordMazeGridGeneratorService } from './word-maze-grid-generator.service';

@Injectable({
	providedIn: 'root',
})
export class EditorGameDefinitionService extends RendererGameDefinitionService {
	public get editorGameDefinitions(): EditorGameDefinition[] {
		return this._editorGameDefinitions;
	}

	private _editorGameDefinitions: EditorGameDefinition[] = [];

	constructor(private wordMazeGridGenerator: WordMazeGridGeneratorService) {
		super();
		this.initializeGameDefinitions();
	}

	private async initializeGameDefinitions(): Promise<void> {
		this._editorGameDefinitions = await this.getEditorGameDefinitions();
	}

	/**
	 * Get editor game definitions with editor-specific metadata
	 * This creates EditorGameDefinition objects based on the renderer registry
	 */
	async getEditorGameDefinitions(): Promise<EditorGameDefinition[]> {
		const availableGameTypes = this.getAvailableGameTypes();

		const editorDefinitions: EditorGameDefinition[] = [];

		for (const gameType of availableGameTypes) {
			const baseDefinition = await this.getGameDefinition(gameType.type);
			if (!baseDefinition) continue;

			const editorGameDefinition: EditorGameDefinition = {
				// Base renderer properties
				type: baseDefinition.type,
				title: baseDefinition.title,
				description: baseDefinition.description,
				displayMode: baseDefinition.displayMode,
				defaultDisplayMode: baseDefinition.defaultDisplayMode,
				rendererComponent: baseDefinition.rendererComponent,
				gameDataType: GameData as unknown as new () => GameData,

				// Editor-specific properties
				editorComponent: undefined,
				state: GameProductionState.Prototype,
				thumbnailClass: '',
				isInternal: false,
				tags: [],
				categories: [],
				presets: [] as EditorGameDefinitionPreset[],
				supportAutoGeneration: false,
				supportAutoGenerationWithManualSettings: false,
			};

			// Set editor-specific metadata for each game type
			this.setEditorSpecificProperties(editorGameDefinition);

			editorDefinitions.push(editorGameDefinition);
		}

		return editorDefinitions;
	}

	/**
	 * Get a single editor game definition by type
	 */
	async getEditorGameDefinition(
		gameType: PluginType,
	): Promise<EditorGameDefinition | undefined> {
		const definitions = await this.getEditorGameDefinitions();
		return definitions.find((def) => def.type === gameType);
	}

	/**
	 * Set editor-specific properties for each game type
	 */
	private setEditorSpecificProperties(
		editorGameDefinition: EditorGameDefinition,
	): void {
		switch (editorGameDefinition.type) {
			case PluginType.Template:
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-template-editor/plugin-template-editor.component'
					).then((m) => m.PluginTemplateEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.mobileSupport = false;
				editorGameDefinition.tags = [];
				editorGameDefinition.categories = [];
				break;

			case PluginType.MultiChoice:
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-multi-choice-editor/plugin-multi-choice-editor.component'
					).then((m) => m.PluginMultiChoiceEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Production;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.thumbnailClass = 'bi bi-question-circle';
				editorGameDefinition.cardBackgroundColor = '#3A82B6';
				editorGameDefinition.gamePreview = {
					type: PluginType.MultiChoice,
					title: 'Leffatrivia',
					questions: [
						{
							questionText:
								'Mikä on maailman kaikkien aikojen tuottoisin elokuva?',
							answers: [
								{ text: 'Avatar', isCorrect: true },
								{ text: 'Titanic', isCorrect: false },
								{ text: 'Avengers: Endgame', isCorrect: false },
								{ text: 'Star Wars', isCorrect: false },
							],
							explanationText:
								'Oikea vastaus on Avatar (2009), joka on tuottanut yli 2,9 miljardia dollaria maailmanlaajuisesti.',
							correctExplanationText:
								'Oikein! Avatar on kaikkien aikojen tuottoisin elokuva.',
						},
						{
							questionText:
								'Kuka ohjasi "Taru sormusten herrasta" -trilogian?',
							answers: [
								{ text: 'Peter Jackson', isCorrect: true },
								{ text: 'Steven Spielberg', isCorrect: false },
								{ text: 'Christopher Nolan', isCorrect: false },
								{ text: 'James Cameron', isCorrect: false },
							],
							explanationText:
								'Oikea vastaus on Peter Jackson, joka ohjasi kaikki kolme Taru sormusten herrasta -elokuvaa.',
							correctExplanationText:
								'Oikein! Peter Jackson ohjasi koko trilogian.',
						},
						{
							questionText:
								'Mikä oli Disneyn ensimmäinen pitkä animaatioelokuva?',
							answers: [
								{
									text: 'Lumikki ja seitsemän kääpiötä',
									isCorrect: true,
								},
								{ text: 'Pinocchio', isCorrect: false },
								{ text: 'Fantasia', isCorrect: false },
								{ text: 'Bambi', isCorrect: false },
							],
							explanationText:
								'Oikea vastaus on Lumikki ja seitsemän kääpiötä (1937), joka oli Disneyn ensimmäinen pitkä animaatioelokuva.',
							correctExplanationText:
								'Oikein! Lumikki oli Disneyn ensimmäinen pitkä animaatioelokuva.',
						},
						{
							questionText:
								'Kuinka monta Oscar-palkintoa "Titanic" voitti?',
							answers: [
								{ text: '11', isCorrect: true },
								{ text: '8', isCorrect: false },
								{ text: '13', isCorrect: false },
								{ text: '9', isCorrect: false },
							],
							explanationText:
								'Oikea vastaus on 11 Oscar-palkintoa, mukaan lukien parhaan elokuvan ja parhaan ohjaajan palkinnot.',
							correctExplanationText:
								'Oikein! Titanic voitti 11 Oscaria.',
						},
						{
							questionText:
								'Mihin maahan "Gladiaattori" sijoittuu?',
							answers: [
								{
									text: 'Rooman valtakuntaan',
									isCorrect: true,
								},
								{ text: 'Kreikkaan', isCorrect: false },
								{ text: 'Egyptiin', isCorrect: false },
								{ text: 'Persiaan', isCorrect: false },
							],
							explanationText:
								'Oikea vastaus on Rooman valtakunta. Elokuva kertoo gladiaattorista antiikin Roomassa.',
							correctExplanationText:
								'Oikein! Gladiaattori sijoittuu antiikin Roomaan.',
						},
					],
					displayMode: 'sequential',
					selectedInterfaceId: PluginInterfaceId.Interface2, // Neutral
				} as MultiChoiceGameData;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.Entertainment,
					GameTag.FamiliarGames,
					GameTag.Educational,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;

			// case PluginType.WordBoxQuiz:
			// 	editorGameDefinition.editorComponent = () =>
			// 		import(
			// 			'../plugin-word-box-quiz-editor/plugin-word-box-quiz-editor.component'
			// 		).then((m) => m.PluginWordBoxQuizEditorComponent);
			// 	editorGameDefinition.displayMode = 'both';
			// 	editorGameDefinition.defaultDisplayMode = 'modal';
			// 	editorGameDefinition.state = GameProductionState.Deprecated;
			// 	editorGameDefinition.thumbnailClass = 'bi bi-puzzle';
			// 	editorGameDefinition.gamePreview = {
			// 		type: PluginType.WordBoxQuiz,
			// 		words: [
			// 			{
			// 				word: 'demokratia',
			// 				hint: 'Hallintojärjestelmä, jossa valta kuuluu kansalle',
			// 			},
			// 			{
			// 				word: 'fotosynteesi',
			// 				hint: 'Prosessi, jossa kasvit tuottavat sokeria auringonvalosta',
			// 			},
			// 			{
			// 				word: 'algoritmi',
			// 				hint: 'Vaiheittainen menetelmä ongelman ratkaisemiseen',
			// 			},
			// 			{
			// 				word: 'infrastruktuuri',
			// 				hint: 'Yhteiskunnan toiminnan kannalta välttämättömät rakenteet ja palvelut',
			// 			},
			// 		],
			// 	} as WordBoxQuizGameData;
			// 	editorGameDefinition.tags = [GameTag.Component];
			// 	editorGameDefinition.categories = [GameCategory.EasyGames];
			// 	break;

			case PluginType.WordMerge:
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-word-merge-editor/plugin-word-merge-editor.component'
					).then((m) => m.PluginWordMergeEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.thumbnailClass = 'bi bi-node-plus';
				editorGameDefinition.cardBackgroundColor = '#6f42c1';
				editorGameDefinition.gamePreview = {
					type: PluginType.WordMerge,
					gridSize: 4,
					spawnRate: 3000,
					targetScore: 20,
					categories: [
						{
							name: 'Hedelmät',
							words: [
								'omena',
								'banaani',
								'appelsiini',
								'viinirypäle',
								'mansikka',
							],
						},
						{
							name: 'Värit',
							words: [
								'punainen',
								'sininen',
								'keltainen',
								'vihreä',
								'violetti',
							],
						},
						{
							name: 'Eläimet',
							words: [
								'koira',
								'kissa',
								'lintu',
								'kala',
								'hevonen',
							],
						},
					],
				} as PluginWordMergeGameData;
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.mobileSupport = false;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.Entertainment,
					GameTag.FamiliarGames,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;

			case PluginType.SequenceOrdering: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-sequence-ordering-editor/plugin-sequence-ordering-editor.component'
					).then((m) => m.PluginSequenceOrderingEditorComponent);
				editorGameDefinition.displayMode = 'both';

				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;

				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Beta;
				editorGameDefinition.thumbnailClass = 'bi bi-sort-numeric-down';
				editorGameDefinition.cardBackgroundColor = '#2E9B6C';
				const sequenceDemo = new SequenceOrderingGameData();
				sequenceDemo.title = 'Maailman mullistuksia viime vuosisadalta';
				sequenceDemo.description =
					'Osaatko järjestää tapahtumat aikajärjestykseen vanhimmasta uusimpaan? Testaa tietosi!';
				sequenceDemo.theme = '';
				sequenceDemo.items = [
					{
						id: 1,
						text: 'Ensimmäinen televisiolähetys',
						imageId: -1,
					},
					{
						id: 2,
						text: 'Penisilliinin löytäminen',
						imageId: -1,
					},
					{
						id: 3,
						text: 'YK:n perustaminen',
						imageId: -1,
					},
					{
						id: 4,
						text: 'DNA:n rakenteen selvittäminen',
						imageId: -1,
					},
					{
						id: 5,
						text: 'Ensimmäinen ihminen avaruudessa',
						imageId: -1,
					},
					{
						id: 6,
						text: 'Ihmisen kuulento',
						imageId: -1,
					},
					{
						id: 7,
						text: 'Internet avataan yleiseen käyttöön',
						imageId: -1,
					},
					{
						id: 8,
						text: 'Kansainvälinen avaruusasema ISS aloittaa toimintansa',
						imageId: -1,
					},
				];
				sequenceDemo.explanationText =
					'Mahtavaa! Tapahtumat ovat oikeassa aikajärjestyksessä.';
				sequenceDemo.useHealth = false;
				sequenceDemo.usePoints = true;
				sequenceDemo.pointsPerCorrect = 10;
				sequenceDemo.pointsPerIncorrect = -2;
				sequenceDemo.maxPoints = 50;
				sequenceDemo.selectedInterfaceId = PluginInterfaceId.Interface2;
				editorGameDefinition.gamePreview = sequenceDemo;

				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.Educational,
					GameTag.Entertainment,
					GameTag.FamiliarGames,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;
			}

			case PluginType.GridBoard:
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-grid-board-editor/plugin-grid-board-editor.component'
					).then((m) => m.PluginGridBoardEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Alpha;
				editorGameDefinition.thumbnailClass = 'bi bi-grid-3x3';
				editorGameDefinition.gamePreview = {
					type: PluginType.GridBoard,
					title: 'Matematiikan seikkailu',
					rows: 3,
					columns: 3,
					initialHealth: 3,
					gridItems: [
						{
							id: '1',
							difficulty: 1,
							color: 'green',
							pluginType: PluginType.MultiChoice,
							completed: false,
							isStartGrid: true,
						},
						{
							id: '2',
							difficulty: 1,
							color: 'green',
							pluginType: PluginType.WordBoxQuiz,
							completed: false,
						},
						{
							id: '3',
							difficulty: 2,
							color: 'yellow',
							pluginType: PluginType.SequenceOrdering,
							completed: false,
						},
						{
							id: '4',
							difficulty: 2,
							color: 'yellow',
							pluginType: PluginType.MultiChoice,
							completed: false,
						},
						{
							id: '5',
							difficulty: 3,
							color: 'red',
							pluginType: PluginType.CrosswordPuzzle,
							completed: false,
						},
					] as GridItem[],
				} as GridBoardGameData;
				editorGameDefinition.tags = [
					GameTag.Container,
					GameTag.Entertainment,
					GameTag.FamiliarGames,
				];
				editorGameDefinition.categories = [];
				editorGameDefinition.presets = [
					{
						name: 'Oma preset',
						description:
							'Tämä on testipreset, joka ei sisällä mitään.',
						state: GameProductionState.Prototype,
						gameData: new GridBoardGameData(),
						thumbnailClass: 'bi bi-circle',
						tags: [GameTag.Preset],
						categories: [],
					} as EditorGameDefinitionPreset,
				];
				break;

			case PluginType.PredictionTrivia:
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-prediction-trivia-editor/plugin-prediction-trivia-editor.component'
					).then((m) => m.PluginPredictionTriviaEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Beta;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.thumbnailClass = 'bi bi-patch-question';
				editorGameDefinition.cardBackgroundColor = '#8B6BB8';
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.gamePreview = {
					type: PluginType.PredictionTrivia,
					gameName: 'Tomaatinviljelyn opas',
					introText:
						'<p>Tomaatti on yksi maailman suosituimmista kasviksista, joka menestyy erilaisissa kasvuolosuhteissa. Onnistunut tomaatinviljely vaatii oikeaa lajivalintaa, sopivaa maaperänhoitoa ja tarkkaa huomiota kasvin tarpeisiin. Tässä oppaassa tutustut tomaattien viljelyyn, erilaisiin lajikkeisiin ja parhaimpiin kasvutekniikoihin.</p>',
					paragraphs: [
						{
							title: 'Tomaattilajikkeiden monimuotoisuus',
							paragraph:
								'<p>Tomaattilajikkeita on satoja erilaisia, ja ne voidaan jakaa useisiin pääkategorioihin kasvutavan, koon ja muodon mukaan. Määräkasvuiset lajikkeet (determinate) kasvavat tiettyyn korkeuteen ja tuottavat sadot kerralla, kun taas jatkuvakasvuiset (indeterminate) jatkavat kasvuaan ja satomuodostusta koko kasvukauden ajan. Hedelmien koon mukaan lajikkeet jaetaan kirsikoiksi, kerrytomatteiksi, keskikokoisiksi ja suuriksi lihatomaateiksi. Värikin vaihtelee perinteisestä punaisesta keltaiseen, vihreään, violettiin ja jopa lähes mustaan.</p>',
							question:
								'Mikä ero on määräkasvuisilla ja jatkuvakasvuisilla tomaateilla?',
							answers: [
								'Määräkasvuiset kasvavat korkeammiksi',
								'Jatkuvakasvuiset tuottavat sadot kerralla',
								'Määräkasvuiset pysäyttävät kasvun tietyssä korkeudessa',
							],
							correctAnswer: 2,
							userAnswer: -1,
						},
						{
							title: 'Optimaaliset kasvuolosuhteet ja maaperä',
							paragraph:
								'<p>Tomaatit vaativat menestymiseen lämpöä, aurinkoa ja ravikasta, hyvin kuivattuvaa maata. Optimaalinen kasvulämpötila on 20-25°C päivällä ja 15-18°C yöllä. Maan pH-arvon tulisi olla 6,0-7,0, ja maaperän tulee olla humuspitoista ja hyvin mullattu. Tomaatit tarvitsevat paljon typpeä lehvien kasvuun, fosforia juuriston kehitykseen ja kaliumia hedelmien laatuun. Säännöllinen kastelu on tärkeää, mutta liiallinen kosteus voi aiheuttaa juurimätää ja sienitauteja. Mulkkauksella voidaan pitää kosteus tasaisena ja estää rikkakasvien kasvu.</p>',
							question:
								'Mikä on tomaattien optimaalinen päivälämpötila?',
							answers: ['15-18°C', '20-25°C', '25-30°C'],
							correctAnswer: 1,
							userAnswer: -1,
						},
						{
							title: 'Tukeminen ja hoitotoimenpiteet',
							paragraph:
								'<p>Jatkuvakasvuiset tomaatit tarvitsevat tuen kasvuun, sillä ne voivat kasvaa jopa 2-3 metriä korkeiksi. Tukimenetelmiä ovat kepit, häkit, köysituki tai spaljerit. Versominen eli sivuversien poistaminen on tärkeää jatkuvakasvuisille lajikkeille, jotta energia keskittyy päävarteen ja hedelmien kehitykseen. Kukkien pölyttymistä voidaan parantaa suhisuttamalla kukkivia kasveja kevyesti. Sadonkorjuu aloitetaan, kun hedelmät alkavat saada väriä, ja ne voivat kypsyä myös poiminnan jälkeen. Säännöllinen sadonsaato kannustaa kasvia tuottamaan lisää kukkia ja hedelmiä.</p>',
							question:
								'Miksi jatkuvakasvuisia tomaatteja versoitetaan?',
							answers: [
								'Jotta ne eivät kasva liian korkeiksi',
								'Jotta energia keskittyy päävarteen ja hedelmiin',
								'Jotta ne kestävät paremmin kuivuutta',
							],
							correctAnswer: 1,
							userAnswer: -1,
						},
					] as TriviaParagraph[],
					selectedInterfaceId: PluginInterfaceId.Interface1,
				} as PredictionTriviaGameData;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.StandaloneEducational,
					GameTag.Educational,
					GameTag.InnovativeGames,
				];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;

			case PluginType.StudyMaterialSummary:
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-study-material-summary-editor/plugin-study-material-summary-editor.component'
					).then((m) => m.PluginStudyMaterialSummaryEditorComponent);
				editorGameDefinition.displayMode = 'fullscreen';
				editorGameDefinition.defaultDisplayMode = 'fullscreen';
				editorGameDefinition.state = GameProductionState.Production;
				editorGameDefinition.isInternal = true;
				editorGameDefinition.thumbnailClass = 'bi-blockquote-left';
				editorGameDefinition.cardBackgroundColor = '#4A7C9C';
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.gamePreview = {
					type: PluginType.StudyMaterialSummary,
					title: 'Kestävä kehitys',
					selectedInterfaceId: PluginInterfaceId.Interface1,
					summary:
						'Kattava yhteenveto kestävän kehityksen periaatteista ja soveltamisesta yhteiskunnassa',
					topics: [
						{
							title: 'Ympäristön kestävyys',
							summary:
								'Luonnon monimuotoisuuden säilyttäminen ja ilmastonmuutoksen torjunta',
							intendation: 0,
							paragraphs: [
								(() => {
									const paragraph =
										new StudyMaterialSummaryEditorParagraph();
									const textData = new TextRendererGameData();
									textData.text =
										'Ympäristön kestävyys on yksi kestävän kehityksen kolmesta peruspilarista. Se keskittyy luonnonvarojen kestävään käyttöön ja ekosysteemien suojeluun. Ilmastonmuutos on yksi suurimmista ympäristöhaasteista, joka vaatii välittömiä toimenpiteitä hiilidioksidipäästöjen vähentämiseksi. Luonnon monimuotoisuuden säilyttäminen on myös keskeistä, sillä se takaa ekosysteemien toimivuuden ja resilienssiin. Ympäristön kestävyyden edistäminen vaatii yhteistyötä yksilöiden, yritysten ja hallitusten välillä.';
									paragraph.gameData = textData;
									return paragraph;
								})(),
							],
							tags: [
								{
									name: 'ympäristö',
									description: 'Ympäristöasiat',
								},
							],
						},
						{
							title: 'Sosiaalinen kestävyys',
							summary:
								'Ihmisten hyvinvointi ja yhteiskunnallinen oikeudenmukaisuus',
							intendation: 0,
							paragraphs: [
								(() => {
									const paragraph =
										new StudyMaterialSummaryEditorParagraph();
									const textData = new TextRendererGameData();
									textData.text =
										'Sosiaalinen kestävyys käsittää yhteiskunnallisen oikeudenmukaisuuden, tasa-arvon ja kaikkien ihmisten perusoikeuksien turvaamisen. Se tarkoittaa, että kaikilla on yhtäläiset mahdollisuudet koulutukseen, terveydenhuoltoon ja hyvinvointiin. Yhteisöllisyys ja osallisuus ovat tärkeitä sosiaalisessa kestävyydessä, sillä ne vahvistavat yhteiskunnan koheesiota. Työllisyys, turvallisuus ja kulttuurinen monimuotoisuus ovat myös keskeisiä tekijöitä sosiaalisessa kestävyydessä.';
									paragraph.gameData = textData;
									return paragraph;
								})(),
							],
							tags: [
								{
									name: 'sosiaaliset',
									description: 'Sosiaaliset näkökohdat',
								},
							],
						},
						{
							title: 'Taloudellinen kestävyys',
							summary:
								'Vastuullinen taloudellinen toiminta ja resurssien tehokas käyttö',
							intendation: 0,
							paragraphs: [
								(() => {
									const paragraph =
										new StudyMaterialSummaryEditorParagraph();
									const textData = new TextRendererGameData();
									textData.text =
										'Taloudellinen kestävyys tarkoittaa vastuullista ja pitkäjänteistä taloudellista toimintaa, joka ei vaaranna tulevien sukupolvien mahdollisuuksia. Se sisältää resurssien tehokkaan käytön, kiertotalouden periaatteet ja investoinnit kestäviin ratkaisuihin. Taloudellinen kasvu ei saa tapahtua ympäristön tai sosiaalisen hyvinvoinnin kustannuksella. Vastuullinen liiketoiminta ja eettinen kuluttaminen ovat tärkeitä keinoja edistää taloudellista kestävyyttä. Innovaatiot ja teknologian kehitys voivat tukea kestävää taloutta luomalla uusia, resurssitehokkaita ratkaisuja.';
									paragraph.gameData = textData;
									return paragraph;
								})(),
							],
							tags: [
								{
									name: 'talous',
									description: 'Taloudelliset näkökohdat',
								},
							],
						},
					] as StudyMaterialSummaryEditorTopic[],
				} as StudyMaterialSummaryGameData;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.StandaloneEducational,
				];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;

			case PluginType.DetectiveCaseStudy: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-detective-case-study-editor/plugin-detective-case-study-editor.component'
					).then((m) => m.PluginDetectiveCaseStudyEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Alpha;
				editorGameDefinition.thumbnailClass = 'bi bi-search';

				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.desktopSupport = true;

				const detectiveDemo = new PluginDetectiveCaseStudyGameData();
				detectiveDemo.title =
					'Katoava jälki suolla – Arvoitus Tiilikkajärven kansallispuistossa';
				detectiveDemo.mystery =
					'Selvitä, miksi harvinainen eläin on jättänyt outoja jälkiä Tiilikkajärven kansallispuistoon ja mitä sinne kulkijoilta piiloon jäänyt ilmiö voi paljastaa luonnonystävistä.';
				detectiveDemo.document = [
					{
						type: 'paragraph',
						text: 'Tiilikkajärven kansallispuiston pitkospuilta löytyi varhain aamulla tuoreita, outoja jalanjälkiä. Maastoa tarkastellessa huomattiin, että jäljet eivät olleet aivan tavallisia: ne muistuttivat sekä karhun että kanadanmajavan askelia. Lisäksi läheltä Sammakkotammen pysäköintialuetta löytyi sulanut lumikasa, jonka keskellä oli karhun karvoja ja vyötärön korkeudella oleva jälki pitkospuiden kaiteessa. Metsänvartija Forsberg kertoi, että alueella viihtyy kyy, karhu ja kanadanmajava, mutta paikallinen luontokuvaaja on ilmoittanut nähneensä myös itselleen vieraan eläimen varjoa. Mysteeri syvenee, kun Uiton kierron suoalueella nähdään harmaa vilaus – voiko olla, että kuvissa on tallentunut jotakin erityisen ainutlaatuista? Tutki johtolankoja ja löydä, mikä eläin harhailee kansallispuiston suolla, miksi jäljet ovat erikoisia ja mitä muita yllättäviä todisteita löytyy luonnosta.',
					},
				];
				detectiveDemo.clues = {
					clue1: 'Outoja jalanjälkiä pitkospuiden läheisyydessä',
					clue2: 'Sulanut lumikasa, josta löytyi karhun karvoja',
					clue3: 'Majavan rakentama pato suoreunan vieressä',
					clue4: 'Paikallisen luontokuvaajan ilmoitus tuntemattomasta eläimestä',
					clue5: 'Kuvissa näkyvä harmaa vilaus Uiton kierron suoalueella',
				};
				detectiveDemo.solutionClueCount = 5;
				detectiveDemo.solutionText =
					'Tiilikkajärven kansallispuiston arvoitus ratkeaa kuvamateriaalin, jälkien ja luontokuvaajan havainnon perusteella: alueella on nähty kultasakaali, joka on suhteellisen uusi vieras Suomen luonnossa. Jalanjäljet selittyvät kultasakaalin ja muiden lajien liikkumisella samassa maastossa. Sulanut lumikasa paljastaa karhun kevätliikkeitä ja majavan rakennelmat kertovat, että luonto on alueella hyvin monimuotoista – mutta juuri harmaa vilaus varmistaa kultasakaalin läsnäolon luonnossa. Salaperäinen eläin tekee Tiilikkajärven käynnistä jännittävän ja opettaa havainnoimaan luontoympäristöä tarkemmin.';
				detectiveDemo.interactiveElements = [
					{
						id: 'examine1',
						type: 'examine',
						word: 'outoja jalanjälkiä',
						clueId: 'clue1',
					},
					{
						id: 'censored1',
						type: 'censored',
						word: 'karhun karvoja',
						clueId: 'clue2',
					},
					{
						id: 'examine2',
						type: 'examine',
						word: 'Majavan rakentama pato',
						clueId: 'clue3',
					},
					{
						id: 'censored2',
						type: 'censored',
						word: 'tuntemattomasta eläimestä',
						clueId: 'clue4',
					},
					{
						id: 'plugin1',
						type: 'plugin',
						word: 'harmaa vilaus',
						clueId: 'clue5',
						pluginType: 120,
						pluginData: {
							type: 120,
							questions: [
								{
									questionText:
										'Mikä eläin mainittiin uutena vieraana Tiilikkajärven kansallispuistossa?',
									answers: [
										{
											text: 'Kultasakaali',
											isCorrect: true,
										},
										{ text: 'Ilves', isCorrect: false },
										{
											text: 'Poronvasikka',
											isCorrect: false,
										},
										{ text: 'Piisami', isCorrect: false },
									],
									explanationText:
										'Kultasakaali on mainittu uutena, harvinaisena vieraana alueella.',
									correctExplanationText:
										'Oikein! Kultasakaali on viime vuosina tallentunut alueen kameroihin.',
									displayMode: 'sequential',
									selectedInterfaceId:
										PluginInterfaceId.Interface2,
								},
							],
							displayMode: 'sequential',
							selectedInterfaceId: PluginInterfaceId.Interface2,
						} as any,
					},
				];
				editorGameDefinition.gamePreview = detectiveDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.StandaloneEducational,
					GameTag.InnovativeGames,
					GameTag.Entertainment,
				];
				editorGameDefinition.categories = [GameCategory.AdvancedGames];
				break;
			}

			case PluginType.Detective: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-detective-editor/plugin-detective-editor.component'
					).then((m) => m.PluginDetectiveEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Alpha;
				editorGameDefinition.thumbnailClass = 'bi bi-search';
				editorGameDefinition.cardBackgroundColor = '#C65D5D';

				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;

				const detectiveDemo = new PluginDetectiveGameData();
				detectiveDemo.title =
					'Varastetun renessanssimuotokuvan mysteeri';
				detectiveDemo.mystery =
					'Korvaamaton 1500-luvun muotokuva katosi Museo Nacionalista yön aikana. Ei hälytyksiä, ei murtautumisen jälkiä. Kolme epäiltyä, lukemattomia kysymyksiä.';
				detectiveDemo.introText =
					"15. maaliskuuta 2024 Museo Nacionalin arvostettu renessanssimuotokuva, 'Nainen helmikorvakorussa' -kopio, katosi jälkeä jättämättä. Turvajärjestelmät eivät havainneet murtautumista: ovet pysyivät lukittuina, mutta maalaus oli poissa.\n\nKuka järjesti tämän hienostuneen taideryöstön ja miten se toteutettin?";

				detectiveDemo.caseFiles = [
					{
						title: 'Museon turvallisuusraportti',
						content: `PÄIVÄMÄÄRÄ: 15. maaliskuuta 2024
SIJAINTI: Museo Nacional, Galleria 3
YKSITYISKOHDAT: Renessanssimuotokuva "Nainen helmikorvakorussa" (kopio, arvioitu arvo 250 000 €) havaittiin kadonneeksi klo 6:15 rutiininomaisen tarkastuksen aikana. Viimeisin vahvistettu havainto: klo 23:47 yövartijan kierroksen aikana. Murtautumista ei havaittu. Kaikki ovet pysyivät lukittuina. Valvontatallenteissa klo 23:50–6:00 välisenä aikana näkyy valvontakameran häiriö ainoastaan Galleriassa 3. Maalauksen turvakehys kytkettiin pois päältä erikoisavaimella klo 2:34—vahvistettu kulkulokien perusteella. Varastosta löydettiin seinää vasten nojaamassa huonolaatuinen kopiomaalaus, mikä viittaa ennalta suunniteltuun tekoon.`,
					},
					{
						title: 'Epäiltyjen profiili',
						content: `PÄÄEPÄILLYT:
1. Tohtori Elena Voss: Museon kuraattori ja taide-ekspertti, 15 vuoden kokemus alalta. Hän oli poissaollut varkauden yönä väittäen sairastavansa. Hänen kulkukorttinsa kuitenkin osoittaa pääsyä hallinnolliseen toimistoon klo 2:15. Hänellä on yksityiskohtainen tieto kaikista turvaprotokollista ja hänellä on erikoisavain.

2. Marcus Holt: Yövuorossa ollut vartija. Hän raportoi häiriöstä välittömästi, mutta myöntää olleensa taukohuoneessa kriittisen ajanjakson aikana. Hänen talouskirjanpitonsa paljastaa merkittäviä velkoja ja tuoreen rahasopimuksen tuntemattoman offshore-tilin kanssa 50 000 €:n arvosta kaksi viikkoa ennen varkautta.

3. Viktor Armitage: Varakas yksityinen taiteen keräilijä, joka vieraili museossa kolme kertaa varkautta edeltävän kuukauden aikana. Hän tiedusteli erityisesti tämän maalauksen turvatoimia ja hänen kuultiin kysyvän henkilökunnalta maalauksen "todellisesta alkuperästä". Hänellä on sekä resurssit että yhteydet järjestää tällainen hienostunut ryöstö.`,
					},
				];

				detectiveDemo.clues = [
					{
						id: 'clue1',
						name: 'Valvontakameran häiriö',
						description:
							'Valvontakameran toimintahäiriö kriittisen ajanjakson aikana.',
						keyword: {
							word: 'valvontakameran häiriö',
							caseFileIndex: 0,
						},
						caseFileIndex: 0,
					},
					{
						id: 'clue2',
						name: 'Erikoisavain',
						description:
							'Erikoisavainta käytettiin turvakehyksen kytkemiseen pois päältä.',
						keyword: { word: 'erikoisavain', caseFileIndex: 1 },
						caseFileIndex: 1,
					},
					{
						id: 'clue3',
						name: 'Kopiomaalaus',
						description:
							'Varastosta löytynyt huonolaatuinen kopio, todiste etukäteissuunnittelusta.',
						keyword: {
							word: 'kopiomaalaus',
							caseFileIndex: 0,
						},
						caseFileIndex: 0,
					},
					{
						id: 'clue4',
						name: 'Poissaollut',
						description:
							'Tohtori Voss väitti olleensa poissa, mutta kulkukortti osoittaa pääsyn rakennukseen.',
						keyword: { word: 'poissaollut', caseFileIndex: 1 },
						caseFileIndex: 1,
					},
					{
						id: 'clue5',
						name: 'Taide-ekspertti',
						description:
							'Taide-asiantuntija, jolla on yksityiskohtainen tieto turvaprotokollista.',
						keyword: {
							word: 'taide-ekspertti',
							caseFileIndex: 1,
						},
						caseFileIndex: 1,
					},
					{
						id: 'clue6',
						name: 'Rahasopimus',
						description:
							'Vartijan saama, epäilyttävä 50 000 €:n tilisiirto.',
						keyword: {
							word: 'rahasopimuksen',
							caseFileIndex: 1,
						},
						caseFileIndex: 1,
					},
				];

				// Add deduction questions
				detectiveDemo.deductionQuestions = [
					{
						id: 'question1',
						question:
							'Mikä todiste osoittaa, että ryöstö oli sisäpiirityö?',
						correctFirstClueId: 'clue2',
						correctSecondClueId: 'clue3',
					},
					{
						id: 'question2',
						question:
							'Kenellä epäillyllä oli sekä asiantuntemus että epäilyttävä pääsy?',
						correctFirstClueId: 'clue4',
						correctSecondClueId: 'clue5',
					},
					{
						id: 'question3',
						question:
							'Mitkä johtolangat viittaavat epäiltyjen yhteistyöhön?',
						correctFirstClueId: 'clue1',
						correctSecondClueId: 'clue6',
					},
				];

				// Generate deduction slots from correct clues and set required count automatically
				detectiveDemo.generateDeductionSlots();
				detectiveDemo.successMessage =
					'Todisteet osoittavat tohtori Elena Vossiin, taide-eksperttikuraattoriin, joka käytti erikoisavainta ja sisäpiirin tietojaan ryöstön toteuttamiseen. Vartija Marcus Holt oli todennäköisesti hänen rikoskumppaninsa, jolle maksettiin 50 000 € kameroiden sabotoimisesta.';
				detectiveDemo.failureMessage =
					'Teoriassasi on muutamia aukkoja: tarkastele johtopäätöksiäsi uudelleen.';

				editorGameDefinition.gamePreview = detectiveDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.StandaloneEducational,
					GameTag.InnovativeGames,
					GameTag.Entertainment,
				];
				editorGameDefinition.categories = [GameCategory.AdvancedGames];
				break;
			}

			case PluginType.Canvas: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-canvas-editor/plugin-canvas-editor.component'
					).then((m) => m.PluginCanvasEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.thumbnailClass = 'bi bi-palette';

				const canvasDemo = new PluginCanvasGameData();
				canvasDemo.width = 800;
				canvasDemo.height = 600;
				canvasDemo.background = {
					type: 'color',
					value: '#f0f0f0',
				};
				canvasDemo.elements = [
					{
						id: '1',
						type: 'text',
						content: 'Tervetuloa Canvas-demoon!',
						x: 100,
						y: 100,
						width: 300,
						height: 50,
						fontSize: 24,
						color: '#333333',
						inAnimation: 'fade',
						outAnimation: 'none',
						entryDelay: 0,
						inTransitionTime: 500,
						outTransitionTime: 500,
						presentTime: 0,
						zIndex: 1,
					},
					{
						id: '2',
						type: 'text',
						content:
							'Tämä on interaktiivinen piirtoalusta, jossa voit sijoittaa tekstejä ja kuvia vapaasti.',
						x: 100,
						y: 200,
						width: 500,
						height: 100,
						fontSize: 16,
						color: '#666666',
						inAnimation: 'fade',
						outAnimation: 'none',
						entryDelay: 500,
						inTransitionTime: 500,
						outTransitionTime: 500,
						presentTime: 0,
						zIndex: 2,
					},
				];
				canvasDemo.selectedArticles = [];

				editorGameDefinition.gamePreview = canvasDemo;
				editorGameDefinition.tags = [GameTag.Component];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;
			}

			case PluginType.Gallery: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-slider-editor/plugin-slider-editor.component'
					).then((m) => m.PluginSliderEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.thumbnailClass = 'bi bi-image-alt';

				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;

				const galleryDemo = new PluginSliderGameData();
				galleryDemo.slides = [
					{
						pluginType: PluginType.Template,
						pluginData: undefined,
						name: 'Ensimmäinen slide',
					},
					{
						pluginType: PluginType.Template,
						pluginData: undefined,
						name: 'Toinen slide',
					},
				];
				galleryDemo.navigationStyle = 'arrows';
				galleryDemo.transitionStyle = 'slide';

				editorGameDefinition.gamePreview = galleryDemo;
				editorGameDefinition.tags = [
					GameTag.Container,
					GameTag.StandaloneEducational,
					GameTag.FamiliarGames,
				];
				editorGameDefinition.categories = [];
				break;
			}

			case PluginType.TextMessageConversation: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-text-message-conversation-editor/plugin-text-message-conversation-editor.component'
					).then(
						(m) => m.PluginTextMessageConversationEditorComponent,
					);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Alpha;
				editorGameDefinition.thumbnailClass = 'bi bi-chat-dots';

				const textMessageDemo =
					new PluginTextMessageConversationGameData();
				textMessageDemo.receiverName = 'Anna';
				textMessageDemo.initialMessage =
					'Hei! Tiesitätkö että Rooman valtakunta kesti yli 1000 vuotta?';
				textMessageDemo.messages = [
					{
						id: '1',
						sender: 'ai',
						content:
							'Hei! Tiesitätkö että Rooman valtakunta kesti yli 1000 vuotta?',
						contentByStyle: {
							default:
								'Hei! Tiesitätkö että Rooman valtakunta kesti yli 1000 vuotta?',
						},
						timestamp: new Date(2024, 0, 1, 10, 0),
					},
					{
						id: '2',
						sender: 'user',
						content:
							'Joo! Se oli todella pitkäaikainen sivilisaatio.',
						contentByStyle: {
							default:
								'Joo! Se oli todella pitkäaikainen sivilisaatio.',
						},
						timestamp: new Date(2024, 0, 1, 10, 2),
					},
					{
						id: '3',
						sender: 'ai',
						content:
							'Mikä mielestäsi oli sen menestyksen salaisuus?',
						contentByStyle: {
							default:
								'Mikä mielestäsi oli sen menestyksen salaisuus?',
						},
						timestamp: new Date(2024, 0, 1, 10, 5),
					},
				];

				editorGameDefinition.gamePreview = textMessageDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.Entertainment,
					GameTag.StandaloneEducational,
					GameTag.InnovativeGames,
				];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;
			}

			case PluginType.CrosswordPuzzle: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-crossword-puzzle-editor/plugin-crossword-puzzle-editor.component'
					).then((m) => m.PluginCrosswordPuzzleEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'fullscreen';
				editorGameDefinition.state = GameProductionState.Beta;
				editorGameDefinition.thumbnailClass = 'bi bi-grid-3x2';
				editorGameDefinition.cardBackgroundColor = '#D98F3E';

				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.desktopSupport = true;

				editorGameDefinition.supportAutoGeneration = true;

				editorGameDefinition.supportAutoGenerationWithManualSettings = true;

				const crosswordDemo = new CrosswordPuzzleGameData();

				crosswordDemo.colorPalette = undefined;
				crosswordDemo.selectedInterfaceId =
					PluginInterfaceId.Interface1;

				crosswordDemo.mainWords = ['PIZZA'];
				crosswordDemo.secondaryWords = [
					'JUUSTO',
					'PASTA',
					'SALAATTI',
					'LEIPÄ',
					'KEITTO',
					'LIHA',
					'KALA',
					'RIISI',
					'PERUNA',
					'MAKKARA',
					'KAKKU',
					'JÄÄTELÖ',
					'HEDELMÄ',
					'VIHANNEKSET',
				];
				crosswordDemo.mainWordClues = {
					PIZZA: 'Italialainen ruokalaji, jossa on juustoa ja tomaattikastiketta',
				};
				crosswordDemo.secondaryWordClues = {
					JUUSTO: 'Maidosta valmistettu keltainen tai valkoinen ruoka',
					PASTA: 'Italialainen nuudeliruoka',
					SALAATTI: 'Raakoja vihanneksia sisältävä ruokalaji',
					LEIPÄ: 'Viljasta leivottu perusruoka',
					KEITTO: 'Nestemäinen lämmin ruokalaji',
					LIHA: 'Eläimestä saatu proteiinipitoinen ruoka',
					KALA: 'Vedessä elävä eläin, jota syödään',
					RIISI: 'Aasialainen viljalaji, joka keitetään',
					PERUNA: 'Maan alla kasvava mukulavihannes',
					MAKKARA: 'Jauhelihasta valmistettu pitkänomainen ruoka',
					KAKKU: 'Makea leivonnainen, jota syödään jälkiruokana',
					JÄÄTELÖ: 'Pakastettu kylmä ja makea herkku',
					HEDELMÄ: 'Makea ja terveellinen kasvin osa',
					VIHANNEKSET:
						'Terveellisiä kasviksia, joita syödään salaatissa',
				};
				crosswordDemo.mainWordImageClues = {};
				crosswordDemo.secondaryWordImageClues = {};
				crosswordDemo.placedWords = [];
				crosswordDemo.gridRows = 0;
				crosswordDemo.gridCols = 0;
				crosswordDemo.minRow = 0;
				crosswordDemo.maxRow = 0;
				crosswordDemo.minCol = 0;
				crosswordDemo.maxCol = 0;
				crosswordDemo.intersections = [];
				crosswordDemo.selectedArticles = [
					{
						title: 'wabbit.jpg',
						creator: '',
						isMine: true,
						dateCreated: 1755782872,
						dateModified: '1755782826652',
						content: '',
						tags: [],
						relatedArticleIds: [],
						vectorStorageState: 'not_added',
						id: 135,
						analysisTypes: [],
						type: 'image',
						sourceType: 'url',
						url: 'wabbit.jpg',
						size: '1130023',
						metadata: {},
					} as any,
				];
				crosswordDemo.backgroundImageId = {
					title: 'wabbit.jpg',
					creator: '',
					isMine: true,
					dateCreated: 1755782872,
					dateModified: '1755782826652',
					content: '',
					tags: [],
					relatedArticleIds: [],
					vectorStorageState: 'not_added',
					id: 135,
					analysisTypes: [],
					type: 'image',
					sourceType: 'url',
					url: 'wabbit.jpg',
					size: '1130023',
					metadata: {},
				} as any;

				editorGameDefinition.gamePreview = crosswordDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.FamiliarGames,
					GameTag.Entertainment,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;
			}

			case PluginType.CardSorting: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-card-sorting-editor/plugin-card-sorting-editor.component'
					).then((m) => m.PluginCardSortingEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.thumbnailClass = 'bi bi-suit-spade';

				const cardSortingDemo = new CardSortingGameData();
				cardSortingDemo.title = 'Korttien lajittelu';
				cardSortingDemo.description =
					'Lajittele kortit oikeisiin kategorioihin';
				cardSortingDemo.categories = [
					{
						id: '1',
						name: 'Hedelmät',
						color: '#28a745',
					},
					{
						id: '2',
						name: 'Vihannekset',
						color: '#ffc107',
					},
				];
				cardSortingDemo.cards = [
					{
						id: '1',
						text: 'Omena',
						description: 'Punainen hedelmä',
						category: 0,
					},
					{
						id: '2',
						text: 'Porkkana',
						description: 'Oranssi juures',
						category: 1,
					},
					{
						id: '3',
						text: 'Banaani',
						description: 'Keltainen hedelmä',
						category: 0,
					},
				];
				cardSortingDemo.showFeedbackAfterEachCard = true;
				cardSortingDemo.usePoints = true;

				editorGameDefinition.gamePreview = cardSortingDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.InnovativeGames,
					GameTag.Entertainment,
				];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;
			}

			case PluginType.Digest: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-digest-editor/plugin-digest-editor.component'
					).then((m) => m.PluginDigestEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'fullscreen';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.thumbnailClass = 'bi bi-newspaper';

				const digestDemo = new PluginDigestGameData();
				digestDemo.questions = [
					{
						id: '1',
						name: 'Ilmastonmuutos',
						question:
							'Mitä mieltä olet uusimmista ilmastonmuutostoimista?',
						emoji: '🌍',
						tags: ['ympäristö', 'ilmasto'],
					},
					{
						id: '2',
						name: 'Uusiutuva energia',
						question:
							'Onko uusiutuva energia tulevaisuuden ratkaisu?',
						emoji: '⚡',
						tags: ['energia', 'teknologia'],
					},
				];
				digestDemo.newsItems = [
					{
						id: '1',
						title: 'Ilmastonmuutoksen vaikutukset',
						summary:
							'Tutkimus selvittää maapallon lämpenemisen seurauksia',
						links: [{ title: 'Lue lisää', url: '#' }],
						tags: ['ympäristö', 'tutkimus'],
					},
					{
						id: '2',
						title: 'Uusiutuvat energialähteet',
						summary: 'Aurinko- ja tuulivoiman kehitys nopeaa',
						links: [{ title: 'Lue artikkeli', url: '#' }],
						tags: ['energia', 'teknologia'],
					},
				];

				editorGameDefinition.gamePreview = digestDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.StandaloneEducational,
					GameTag.InnovativeGames,
				];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;
			}

			case PluginType.InteractiveReadingExperience:
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-interactive-reading-experience-editor/plugin-interactive-reading-experience-editor.component'
					).then(
						(m) =>
							m.PluginInteractiveReadingExperienceEditorComponent,
					);

				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.thumbnailClass = 'bi bi-book';

				editorGameDefinition.gamePreview = {
					type: PluginType.InteractiveReadingExperience,
					sections: [
						{
							id: '1',
							title: 'Historiallinen Aikajana',
							contentType: SectionContentType.DesignElement,
							designElementType: DesignElementType.Timeline,
							designElementData: {
								type: DesignElementType.Timeline,
								title: 'Historical Timeline',
								subtitle: 'Journey through time',
								events: [
									{
										id: 'event1',
										title: 'First Event',
										date: '2020',
										description:
											'Description of the first event in the timeline.',
									},
									{
										id: 'event2',
										title: 'Second Event',
										date: '2021',
										description:
											'Description of the second event in the timeline.',
									},
									{
										id: 'event3',
										title: 'Third Event',
										date: '2022',
										description:
											'Description of the third event in the timeline.',
									},
								],
								backgroundColor: '#f8f9fa',
								textColor: '#333333',
								accentColor: '#007bff',
								timelineStyle: 'vertical',
								showDates: true,
								animateOnScroll: true,
							},
							pluginType: PluginType.None,
							fadeInAnimation: 'slideInFromLeft',
							fadeOutAnimation: 'slideOutToLeft',
							backgroundColor: '#f8f9fa',
						},
						{
							id: '2',
							title: 'Tilastot ja Luvut',
							contentType: SectionContentType.DesignElement,
							designElementType:
								DesignElementType.AnimatedCounter,
							designElementData: {
								type: DesignElementType.AnimatedCounter,
								title: 'Statistics',
								subtitle: 'Key numbers and metrics',
								counters: [
									{
										id: 'counter1',
										label: 'Users',
										value: 1500,
										suffix: '+',
										color: '#007bff',
										animationDelay: 0,
									},
									{
										id: 'counter2',
										label: 'Projects',
										value: 250,
										suffix: '+',
										color: '#28a745',
										animationDelay: 200,
									},
									{
										id: 'counter3',
										label: 'Success Rate',
										value: 95,
										suffix: '%',
										color: '#ffc107',
										animationDelay: 400,
									},
								],
								layout: 'horizontal',
								animationDuration: 2000,
								animationStyle: 'count-up',
								triggerOnScroll: true,
							},
							pluginType: PluginType.None,
							fadeInAnimation: 'zoomIn',
							fadeOutAnimation: 'zoomOut',
							backgroundColor: '#ffffff',
						},
						{
							id: '3',
							title: 'Kuvagalleria',
							contentType: SectionContentType.DesignElement,
							designElementType: DesignElementType.ImageGallery,
							designElementData: {
								type: DesignElementType.ImageGallery,
								title: 'Image Gallery',
								subtitle: 'Collection of images',
								images: [],
								layout: 'grid',
								columns: 3,
								spacing: 16,
								showCaptions: true,
								enableLightbox: true,
								hoverEffect: 'zoom',
							},
							pluginType: PluginType.None,
							fadeInAnimation: 'slideInFromRight',
							fadeOutAnimation: 'slideOutToRight',
							backgroundColor: '#f5f5f5',
						},
						{
							id: '4',
							title: 'Tietokortit',
							contentType: SectionContentType.DesignElement,
							designElementType: DesignElementType.CardLayout,
							designElementData: {
								type: DesignElementType.CardLayout,
								title: 'Information Cards',
								subtitle: 'Expandable content sections',
								cards: [
									{
										id: 'card1',
										title: 'First Card',
										subtitle: 'Card subtitle',
										content:
											'This is the content of the first card. Click to expand and see more details.',
										backgroundColor: '#ffffff',
										textColor: '#333333',
									},
									{
										id: 'card2',
										title: 'Second Card',
										subtitle: 'Card subtitle',
										content:
											'This is the content of the second card. Click to expand and see more details.',
										backgroundColor: '#ffffff',
										textColor: '#333333',
									},
									{
										id: 'card3',
										title: 'Third Card',
										subtitle: 'Card subtitle',
										content:
											'This is the content of the third card. Click to expand and see more details.',
										backgroundColor: '#ffffff',
										textColor: '#333333',
									},
								],
								layout: 'auto',
								cardStyle: 'default',
								allowExpansion: true,
								showImages: true,
								equalHeight: false,
							},
							pluginType: PluginType.None,
							fadeInAnimation: 'fadeIn',
							fadeOutAnimation: 'fadeOut',
							backgroundColor: '#e3f2fd',
						},
					],
					settings: {
						autoScroll: false,
						scrollSpeed: 800,
						scrollPadding: 20,
						fadeThreshold: 0.3,
						navigationStyle: 'both',
						theme: 'light',
					},
				} as InteractiveReadingExperienceGameData;

				editorGameDefinition.tags = [
					GameTag.Container,
					GameTag.StandaloneEducational,
					GameTag.Educational,
				];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;

			case PluginType.MindMap: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-mind-map-editor/plugin-mind-map-editor.component'
					).then((m) => m.PluginMindMapEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'fullscreen';
				editorGameDefinition.state = GameProductionState.Alpha;
				editorGameDefinition.thumbnailClass = 'bi bi-diagram-3';

				// Create demo mind map data
				const demoMindMap = new MindMapGameData();
				demoMindMap.rootNode = {
					id: 'root',
					text: 'Pompeji - Kuollut kaupunki',
					tooltip:
						'Antiikin Rooman kuuluisa kaupunki, joka tuhoutui Vesuviuksen purkauksessa 79 jKr.',
					details:
						'Pompeji oli kukoistava roomalainen kaupunki Napolin lahden alueella Italiassa. Kaupunki perustettiin noin 600-700 eKr. ja se kukoisti Rooman valtakunnan aikana. Vuonna 79 jKr. Vesuvius-tulivuori purkautui voimakkaasti ja hautasi kaupungin tuhkan alle, säilyttäen sen poikkeuksellisen hyvin jälkipolville. Pompeji on nykyään yksi maailman tärkeimmistä arkeologisista kohteista ja UNESCO:n maailmanperintökohde.',
					level: 0,
					color: 0,
					icon: '🏛️',
					number: '1',
					expanded: true,
					children: [
						{
							id: 'founding',
							text: 'Kaupungin perustaminen ja kehitys',
							tooltip:
								'Pompeijn historia alkuperästä kukoistukseen asti',
							details:
								'Pompeji perustettiin alun perin oskaalaisten toimesta noin 700-600 eKr. Kaupungin nimi tulee todennäköisesti oskan kielestä sanasta "pumpe" (viisi), viitaten viiteen kaupunginosaan. Kaupunki oli strategisesti sijoitettu Sarno-joen suuhun, mikä teki siitä tärkeän kaupankäyntikeskuksen. Kreikkalaiset vaikuttivat kaupunkiin 8.-6. vuosisadalla eKr., minkä jälkeen etruskien hallinta 6.-5. vuosisadalla eKr. Lopulta roomalaiset valloittivat Pompeijn 89 eKr., ja se tuli osaksi Rooman valtakuntaa.',
							level: 1,
							color: 1,
							number: '1',
							icon: '🏗️',
							children: [
								{
									id: 'oscan-period',
									text: '1.1 Oskalainen kausi (700-600 eKr.)',
									tooltip:
										'Kaupungin varhaisimmat asukkaat ja kulttuuri',
									details:
										'Pompeijn alkuperäiset asukkaat olivat oskaaleita, italikkikansaan kuuluvaa kansaa. He rakensivat kaupungin Vesuviuksen rinteelle hedelmälliselle maalle. Oskalaiset olivat maanviljelijöitä ja käsityöläisiä, jotka hyödynsivät tulivuoren tuhkan rikastuttamaa maaperää. Heiltä peräisin oleva kaupungin alkuperäinen suunnitelma näkyy edelleen Pompeijn katurakenteessa.',
									level: 2,
									color: 2,
									number: '1.1',
									icon: '👥',
									children: [],
								},
								{
									id: 'greek-influence',
									text: 'Kreikkalainen vaikutus (8.-6. vs. eKr.)',
									tooltip:
										'Kreikkalaisten kauppiaiden ja kulttuurin vaikutus',
									details:
										'Kreikkalaiset kauppiaat ja uudisasukkaat toivat mukanaan kehittyneen kulttuurin, taiteen ja arkkitehtuurin. He rakensivat ensimmäiset kivirakennukset ja temppelin Apollonille. Kreikkalainen vaikutus näkyy erityisesti kaupungin teatterissa ja julkisissa rakennuksissa. Tänä aikana Pompeji kehittyi merkittäväksi kaupankäyntikeskukseksi Välimeren alueella.',
									level: 2,
									color: 2,
									number: '1.3',
									icon: '🏛️',
									children: [],
								},
								{
									id: 'etruscan-rule',
									text: '1.2 Etrusken hallinta (6.-5. vs. eKr.)',
									tooltip:
										'Etrusken sivilisaation vaikutus kaupungin kehitykseen',
									details:
										'Etruskien hallinta toi mukanaan kehittyneen hallintojärjestelmän ja kaupankäynnin organisoinnin. He rakensivat kaupungin ensimmäisen forum-aukion ja kehittivät viemärijärjestelmän. Etruskin vaikutus näkyy myös uskonnollisissa käytännöissä ja hautaustavoissa. Tänä aikana Pompeji kasvoi merkittäväksi alueelliseksi keskukseksi.',
									level: 2,
									color: 2,
									number: '1.2',
									icon: '👑',
									children: [],
								},
							],
						},
						{
							id: 'roman-period',
							text: 'Roomalainen kausi ja kukoistus',
							tooltip:
								'Pompeijn kultakausi Rooman valtakunnan osana 89 eKr. - 79 jKr.',
							details:
								'Roomalaiset valloittivat Pompeijn 89 eKr. liittolaissotien aikana. Kaupungista tuli roomalainen municipium ja sittemmin colonia. Rooman hallinta toi mukanaan monumentaalisia rakennusprojekteja, kehittyneen infrastruktuurin ja kukoistavan talouden. Kaupunki kasvoi noin 15 000 asukkaan keskukseksi ja tuli suosituksi lomakohteeksi varakkaille roomalaisille. Villojen, kauppojen ja työpajojen määrä kasvoi merkittävästi.',
							level: 1,
							color: 1,
							number: '3',
							icon: '🏺',
							children: [
								{
									id: 'architecture',
									text: 'Arkkitehtuuri ja rakennukset',
									tooltip:
										'Pompeijn upeat julkiset rakennukset ja yksityiskodit',
									details:
										'Roomalainen Pompeji oli arkkitehtuurinen helmi, jossa oli forum, amfiteatteri, kaksi teatteria, lukuisia temppeleitä ja kylpylöitä. Kaupungissa oli yli 600 kauppaa ja työpajaa. Yksityistalot vaihtelivat yksinkertaisista asunnoista ylellisiin villoihin, joissa oli kauniita freeskoja, mosaiikkeja ja puutarhoja. Berühmte Villa dei Papiri oli kirjastonsa ansiosta antiikin ajan kulttuurikeskus.',
									level: 2,
									color: 3,
									number: '3.1',
									icon: '🏛️',
									children: [
										{
											id: 'forum',
											text: 'Forum - Kaupungin sydän',
											tooltip:
												'Pompeijn keskusaukio ja hallinnollinen keskus',
											details:
												'Forum oli Pompeijn tärkein julkinen tila, 38 x 142 metriä laaja aukio, jota ympäröivät tärkeimmät julkiset rakennukset. Siellä käytiin kauppaa, pidettiin poliittisia kokouksia ja oikeudenkäyntejä. Forumia kehystivät basilika (oikeustalo), Apollonin temppeli, Jupiterin temppeli ja lukuisia muita hallinnollisia rakennuksia. Se oli kaupungin sosiaalisen ja taloudellisen elämän keskus.',
											level: 3,
											color: 4,
											number: '3.1.1',
											icon: '🏛️',
											children: [],
										},
										{
											id: 'amphitheater',
											text: 'Amfiteatteri',
											tooltip:
												'Gladiaattorinäytösten ja urheilukilpailujen näyttämö',
											details:
												'Pompeijn amfiteatteri rakennettiin noin 80-70 eKr. ja se oli yksi vanhimmista kiviamfiteattereista. Se vei 20 000 katsojaa ja oli suurempi kuin itse kaupunki olisi tarvinnut, mikä kertoo sen alueellisesta merkityksestä. Amfiteatterissa järjestettiin gladiaattoritaisteluita, villieläintaisteluita ja urheilukilpailuja. Rakennuksen akustiikka oli erinomainen, ja siinä oli kehittynyt käytävä- ja poistumisjärjestelmä.',
											level: 3,
											color: 4,
											number: '3.1.2',
											icon: '⚔️',
											children: [],
										},
									],
								},
								{
									id: 'daily-life',
									text: 'Jokapäiväinen elämä',
									tooltip:
										'Miten pompejalaiset elivät, työskentelivät ja viihtyivät',
									details:
										'Pompejalaiset harjoittivat laajaa kaupankäyntiä, käsityöläisyyttä ja maanviljelyä. Kaupungissa toimi leipomoita, langomoita, värjäämoitä ja metallipajoja. Ihmiset kävivät kylpylöissä, seurasivat teatteriesityksiä ja gladiaattoritaisteluja. Tavernas (kapakat) ja popinait (pikaruokaravintolat) olivat suosittuja kohtaamispaikkoja. Graffitit seinissä kertovat ihmisten ajatuksista, rakkaudesta ja politiikasta.',
									level: 2,
									color: 3,
									number: '3.2',
									icon: '👨‍👩‍👧‍👦',
									children: [
										{
											id: 'trades',
											text: 'Ammatit ja kaupankäynti',
											tooltip:
												'Pompejalaiset ammatti- ja elinkeinoelämä',
											details:
												'Pompeji oli vilkas kauppakeskus, jossa harjoitettiin yli 600 erilaista liiketoimintaa. Tärkeitä ammatteja olivat leipurit (pistrices), lankerit (lanarii), värjärit (infectores), savi-astioiden valmistajat (figuli) ja kultasepät (aurifices). Kaupunki oli kuuluisa viinejään, öljyään ja garum-kalakastiketaan. Monet liikkeet olivat perheyrityksiä, joissa asuttiin kaupan yläkerrassa.',
											level: 3,
											color: 5,
											icon: '🛍️',
											children: [],
										},
										{
											id: 'entertainment',
											text: 'Viihde ja vapaa-aika',
											tooltip:
												'Pompejalaiset huvitukset ja kulttuurielämä',
											details:
												'Pompejalaiset nauttivat monipuolisesta viihteestä. Teatterissa esitettiin komedioita ja tragediaita, amfiteatterissa gladiaattoritaisteluja ja villieläinten metsästystä. Kylpylät (termae) olivat sosiaalisia keskuksia, joissa rentouduttiin, kuntoiltiin ja keskusteltiin. Kaupungissa järjestettiin uskonnollisia festivaaleja, joissa oli prosessioita, uhreja ja juhlintaa. Pelaaminen ja uhkapelit olivat suosittuja.',
											level: 3,
											color: 5,
											icon: '🎭',
											children: [],
										},
									],
								},
							],
						},
						{
							id: 'vesuvius-eruption',
							text: 'Vesuviuksen purkaus 79 jKr.',
							tooltip:
								'Tuhoisa luonnonkatastrofi, joka hautasi Pompeijn ikuisesti',
							details:
								'Vesuvius-tulivuori purkautui 24. elokuuta 79 jKr. (tai mahdollisesti 24. lokakuuta) voimakkaalla pliniaanisella purkauksella. Plinius nuorempi kuvaili tapahtumaa kirjeissään historioitsija Tacitukselle. Purkaus kesti noin 25 tuntia ja hautasi Pompeijn 4-6 metrin paksuisen kerrokseen pumice-kiveä ja tuhkaa. Noin 2000 ihmistä kuoli kaupungissa, ja loput 15 000 asukasta ehti paeta. Kaupunki unohtui 1700 vuodeksi.',
							level: 1,
							color: 1,
							number: '4',
							icon: '🌋',
							children: [
								{
									id: 'warning-signs',
									text: 'Varoitusmerkit ja maanjäristykset',
									tooltip:
										'Vesuviuksen aktivoitumisen ennusmerkit ennen purkausta',
									details:
										'Vesuvius oli ollut lepotilassa satoja vuosia, ja asukkaat eivät pitäneet sitä aktiivisena tulivuorena. Vuonna 62 jKr. voimakas maanjäristys tuhosi monia rakennuksia Pompejissa. Pienempiä maanjäristyksia jatkui vuosina 62-79 jKr., mutta ne tulkittiin erillisiksi ilmiöiksi. Muutamaa päivää ennen purkausta maanjäristykset voimistuivat, mutta asukkaat eivät ymmärtäneet niiden liittyvän tulivuoritoimintaan.',
									level: 2,
									color: 6,
									icon: '⚠️',
									children: [],
								},
								{
									id: 'eruption-phases',
									text: 'Purkauksen vaiheet',
									tooltip:
										'Vesuviuksen purkauksen eteneminen tunti tunnilta',
									details:
										'Purkaus alkoi aamulla räjähdysmäisellä pliniaanisella vaiheella, jolloin 30 km korkea tuhkapilvi nousi ilmaan. Tuuli kuljetti pumice-kiveä ja tuhkaa kohti Pompejia. Iltapäivällä kaupunkiin oli kertynyt jo 2,5 metriä pumice-kiveä. Yöllä alkoivat pyroclastic flows - kuumat kaasu- ja tuhkavirtaukset, jotka saavuttivat nopeuksia 100-700 km/h. Nämä virtaukset tappoivat välittömästi kaikki, jotka olivat vielä kaupungissa.',
									level: 2,
									color: 6,
									icon: '💨',
									children: [
										{
											id: 'plinian-phase',
											text: '4.2.1 Pliniaaninen vaihe (aa 1:00-18:00)',
											tooltip:
												'Purkauksen alkuvaihe: tuhkapilvi ja pumice-sade',
											details:
												'Purkaus alkoi noin kello 13:00 valtavalla räjähdyksellä, joka lähetti magmaa ja kaasua 30 kilometriä ilmakehään. Muodostunut "pinta"-pilvi levisi tuulen mukana kaakkoon kohti Pompejia. Pumice-kivet, putoamisen aikana jäähtyneet magmapalat, alkoivat sataa kaupunkiin. Ensimmäiset tunnit sade oli lievää, mutta voimistui iltapäivää kohden. Monet asukkaat lähtivät pakoon tässä vaiheessa.',
											level: 3,
											color: 7,
											icon: '☁️',
											children: [],
										},
										{
											id: 'pyroclastic-flows',
											text: '4.2.2 Pyroclastic flows (klo 01:00-08:00)',
											tooltip:
												'Kuolemaa tuovat kaasu- ja tuhkavirtaukset',
											details:
												'Yön ja aamun aikana tulivuoren kraateri romahti osittain, mikä aiheutti sarjan pyroclastic flow -virtauksia. Nämä yli 1000°C kuumat kaasu-, tuhka- ja kivensekaokset vyöryivät alas vuorelta 100-700 km/h nopeudella. Ensimmäinen virtaus saavutti Herculaneumin, toinen ja kolmas Pompeijn. Virtaukset tappoivat välittömästi kaikki elävät kuumuuden ja myrkyllisten kaasujen takia.',
											level: 3,
											color: 7,
											icon: '💀',
											children: [],
										},
									],
								},
								{
									id: 'preservation',
									text: 'Kaupungin säilyminen',
									tooltip:
										'Kuinka Pompeji säilyi poikkeuksellisen hyvin jälkipolville',
									details:
										'Vesuviuksen purkaus hautasi Pompeijn 4-6 metrin paksuiseen kerrokseen tuhkaa ja pumice-kiveä. Tämä suojeli kaupungin rakennuksia, esineitä ja jopa ihmisten ruumiita hapettumiselta ja hajoamiselta. Kuuma tuhka muodosti kovan kerroksen, joka säilytti kaiken sisällänsä. Orgaaninen aines mätäni pois, mutta jätti jälkeensä onteloita, joihin Giuseppe Fiorelli 1860-luvulla alkoi valaa kipsiä saadakseen täydelliset jäljennökset uhreista.',
									level: 2,
									color: 6,
									icon: '🏺',
									children: [],
								},
							],
						},
						{
							id: 'rediscovery',
							text: 'Uudelleenlöytö ja kaivaukset',
							tooltip:
								'Pompeijn arkeologinen tutkimus 1700-luvulta nykypäivään',
							details:
								'Pompeji löydettiin uudelleen 1748, kun kuningas Carlos III määräsi aloitettavaksi kaivaukset. Ensimmäiset kaivaukset olivat aarteenetsintää, mutta vähitellen kehittyi tieteellinen arkeologia. Giuseppe Fiorelli modernisoi kaivaukset 1860-luvulla ja kehitti gipsivalutekniikan. Nykyään noin kolme neljäsosaa kaupungista on kaivettu esiin, ja se on UNESCO:n maailmanperintökohde. Kaivaukset jatkuvat edelleen, ja uusia löytöjä tehdään säännöllisesti.',
							level: 1,
							color: 1,
							number: '5',
							icon: '🔍',
							children: [
								{
									id: 'early-excavations',
									text: 'Varhaiset kaivaukset (1748-1860)',
									tooltip:
										'Ensimmäiset aarteenetsijät ja kaivaustyöt',
									details:
										'Carlos III aloitti kaivaukset etsien arvoesineitä kuninkaallisiin kokoelmiinsa. Kaivaustyöt olivat epäjärjestelmällisiä ja tuhoisia - arvoesineet otettiin mukaan, mutta rakennukset jätettiin romahtamaan. Merkittävä löydös oli Villa dei Papiri herculaneumissa 1750-luvulla, jossa oli antiikin maailman suurin säilyneistä kirjastosta. J. J. Winckelmann vieraili kohteessa ja alkoi vaatia tieteellisempää lähestymistapaa.',
									level: 2,
									color: 8,
									icon: '💎',
									children: [],
								},
								{
									id: 'fiorelli-period',
									text: 'Fiorellin aikakausi (1860-1875)',
									tooltip:
										'Tieteellisen arkeologian synty Pompejissa',
									details:
										'Giuseppe Fiorelli mullisti Pompeji-tutkimuksen tuomalla sinne tieteellisen arkeologian. Hän jakoi kaupungin yhdeksään alueeseen (regio) ja numeroi rakennukset järjestelmällisesti. Fiorelli kehitti kuuluisan gipsivalutekniikan 1863, jolla saatiin täydellisiä jäljennöksiä purkauksen uhreista. Hän myös ensimmäisenä ymmärsi säilyttää rakennukset ja esineet in situ alkuperäisessä paikassaan.',
									level: 2,
									color: 8,
									icon: '🏛️',
									children: [],
								},
								{
									id: 'modern-archaeology',
									text: 'Moderni arkeologia (1900-nykyaika)',
									tooltip:
										'Nykyaikaisten tutkimusmenetelmien käyttöönotto',
									details:
										'Amedeo Maiuri johti kaivauksia 1924-1961 ja avasi suuria alueita yleisölle. Hän keskittyi yksityistalojen ja kaupallisen elämän tutkimukseen. Nykyaikaiset menetelmät kuten stratigrafinen kaivaus, laboratoriokokeet ja digitaalinen dokumentointi otettiin käyttöön 1980-luvulta lähtien. 2000-luvulla on käytetty 3D-skannausta, dronevariokuvausta ja GPR-tutkimuksia. Pompeji Archaeological Park perustettiin 2014.',
									level: 2,
									color: 8,
									icon: '💻',
									children: [],
								},
							],
						},
						{
							id: 'legacy',
							text: 'Pompeijn perintö ja merkitys',
							tooltip:
								'Pompeijn vaikutus taiteeseen, tieteeseen ja kulttuuriin',
							details:
								'Pompeji on antanut ainutlaatuisen kuvan antiikin roomalaisten jokapäiväisestä elämästä. Se on inspiroinut lukemattomia taiteilijoita, kirjailijoita ja elokuvaohjaajia. Tieteellisesti Pompeji on mahdollistanut vulkanologian kehityksen ja antanut ymmärrystä pliniaanisista purkauksista. Se on myös muokannut arkeologian menetelmiä ja museotyötä. Nykyään Pompeji on yksi maailman suosituimmista matkailukohteista ja tärkeä tutkimuskeskus.',
							level: 1,
							color: 1,
							number: '6',
							icon: '🎨',
							children: [
								{
									id: 'scientific-importance',
									text: 'Tieteellinen merkitys',
									tooltip:
										'Pompeijn antama panos arkeologiaan ja historiaan',
									details:
										'Pompeji on antanut ennennäkemättömän tarkan kuvan roomalaisesta yhteiskunnasta kaikilla tasoilla. Se on ollut avainasemassa roomalaisen jokapäiväisen elämän, talouden, politiikan ja uskonnon ymmärtämisessä. Graffitit, talouselämän jäännökset ja yksityiskodit ovat paljastaneet asioita, joita historiankirjat eivät kerro. Vulkanologisesti Pompeji on auttanut ymmärtämään tulivuorien toimintaa ja luonnonkatastrofien vaikutuksia.',
									level: 2,
									color: 9,
									icon: '📚',
									children: [],
								},
								{
									id: 'cultural-impact',
									text: 'Kulttuurivaikutus',
									tooltip:
										'Pompeijn inspiratio taiteessa ja populaarikulttuurissa',
									details:
										'Pompeji on inspiroinut lukemattomia taideteoksia 1700-luvulta lähtien. Edward Bulwer-Lyttonin romaani "Pompeijn viimeiset päivät" (1834) teki kaupungista maailmankuulun. Elokuvia on tehty kymmeniä, tunnetuimpina Roberto Rossellinin "Journey to Italy" (1954) ja Paul W.S. Andersonin "Pompeii" (2014). Pompeijn taide ja arkkitehtuuri vaikuttivat neoklassisismiin ja Empire-tyyliin 1700-1800-luvuilla.',
									level: 2,
									color: 9,
									icon: '🎬',
									children: [],
								},
								{
									id: 'modern-challenges',
									text: 'Nykyajan haasteet',
									tooltip:
										'Säilyttämisen ja turismin aiheuttamat ongelmat',
									details:
										'Pompeji kohtaa merkittäviä haasteita: massa matkuru hapaittaa rakenteita, sääolosuhteet ja ilmansaasteet aiheuttavat rappeutumista, ja rahoituspula hidastaa säilytystyötä. Vuosittain noin 2,5 miljoonaa turistia vierailee kohteessa. EU:n Gran Progetto Pompeii -hanke (2012-2019) toi 105 miljoonan euron rahoituksen kiireellisimpiin korjaustöihin. Nykyään kehitetään kestävämpää turisfia ja digitaalisia ratkaisuja kohluksen tulevaisuuden turvaamiseksi.',
									level: 2,
									color: 9,
									icon: '⚖️',
									children: [],
								},
							],
						},
					],
				};

				editorGameDefinition.gamePreview = demoMindMap;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.StandaloneEducational,
					GameTag.InnovativeGames,
				];
				editorGameDefinition.categories = [
					GameCategory.AdaptiveReading,
				];
				break;
			}

			case PluginType.Argumentation: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-argumentation-editor/plugin-argumentation-editor.component'
					).then((m) => m.PluginArgumentationEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.thumbnailClass = 'bi bi-chat-square-text';
				const argumentDemo = new ArgumentationGameData();
				argumentDemo.title = 'Argumenttipeli';
				argumentDemo.description =
					'Rakenna vahvin argumentti annetulle väitteelle valitsemalla parhaat perustelut.';
				argumentDemo.theme = '';
				argumentDemo.claimText =
					'Uusiutuva energia on parempi ratkaisu kuin fossiiliset polttoaineet';
				argumentDemo.items = [
					{
						id: 1,
						text: 'Uusiutuva energia vähentää hiilidioksidipäästöjä merkittävästi',
						points: 5,
						optimalCategory: 'Vahvin perustelu väitteelle',
					},
					{
						id: 2,
						text: 'Aurinko- ja tuulivoiman kustannukset ovat laskeneet',
						points: 4,
						optimalCategory: 'Toiseksi vahvin perustelu',
					},
					{
						id: 3,
						text: 'Uusiutuva energia luo uusia työpaikkoja',
						points: 3,
						optimalCategory: 'Vahvistava todiste',
					},
					{
						id: 4,
						text: 'Energiariippuvuus muista maista vähenee',
						points: 2,
						optimalCategory: 'Vahvistava todiste',
					},
				];
				argumentDemo.argumentCategories = [
					{
						name: 'Vahvin perustelu väitteelle',
						description: '',
						maxItems: 1,
						pointsPerItem: 5,
					},
					{
						name: 'Toiseksi vahvin perustelu',
						description: '',
						maxItems: 1,
						pointsPerItem: 3,
					},
					{
						name: 'Vahvistava todiste',
						description: '',
						maxItems: 1,
						pointsPerItem: 2,
					},
				];
				argumentDemo.maxPoints = 10;
				argumentDemo.feedbackTexts = {
					10: 'Erinomainen argumentti! Valitsit parhaat perustelut.',
					8: 'Hyvä argumentti! Olet ymmärtänyt aiheen hyvin.',
					5: 'Kohtuullinen argumentti. Osa valinnoistasi toimii, mutta voisit parantaa.',
					0: 'Yritä uudestaan. Mieti mitkä perustelut tukisivat väitettä parhaiten.',
				};
				editorGameDefinition.gamePreview = argumentDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.Educational,
					GameTag.Entertainment,
					GameTag.InnovativeGames,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;
			}

			case PluginType.FAQ: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-faq-editor/plugin-faq-editor.component'
					).then((m) => m.PluginFAQEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Prototype;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.thumbnailClass = 'bi bi-question-circle';
				const faqDemo = new PluginFAQGameData();
				faqDemo.title = 'Usein kysyttyä';
				faqDemo.faqItems = [
					{
						id: '1',
						question: 'Mikä on Meta Press?',
						shortAnswer:
							'Meta Press on interaktiivisen sisällön luomiseen tarkoitettu alusta.',
						expandedAnswer:
							'Meta Press on moderni alusta, joka mahdollistaa erilaisten interaktiivisten sisältöjen, kuten pelien ja harjoitusten, luomisen helposti ja tehokkaasti. Alusta tarjoaa monenlaisia työkaluja sisällöntuottajille.',
					},
					{
						id: '2',
						question: 'Miten luon uuden FAQ-pelin?',
						shortAnswer:
							'Valitse FAQ-työkalu ja lisää kysymyksiä ja vastauksia.',
						expandedAnswer:
							'FAQ-pelin luominen on helppoa: valitse FAQ-työkalu työkaluvalikosta, lisää haluamasi kysymykset ja vastaukset. Voit käyttää myös automaattista sisällöntuotantoa lähdemateriaaleista.',
					},
				];
				editorGameDefinition.gamePreview = faqDemo;
				editorGameDefinition.tags = [
					GameTag.Educational,
					GameTag.Component,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;
			}

			case PluginType.WordGuess: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-word-guess-editor/plugin-word-guess-editor.component'
					).then((m) => m.PluginWordGuessEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Beta;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.thumbnailClass = 'bi bi-puzzle';
				editorGameDefinition.cardBackgroundColor = '#5FAA5F';
				const wordGuessDemo = new PluginWordGuessGameData();
				wordGuessDemo.gameTitle = 'Sanapeli';
				wordGuessDemo.wordConfiguration = {
					levels: [
						{
							words: ['KISSA', 'KOIRA'],
							category: 'Kotieläimet',
						},
						{
							words: ['MARS', 'JUPITER', 'SATURNUS'],
							category: 'Planeetat',
						},
						{
							words: ['MAILA', 'JOUKKUE', 'PAITSIO'],
							category: 'Urheilu',
						},
					],
					defaultLevel: 1,
					completionSettings: {
						showProgressBar: true,
						showEndScreen: true,
						endScreenTitle: 'Onneksi olkoon!',
						endScreenText: 'Läpäisit kaikki tasot!',
						playAgainButtonText: 'Pelaa uudelleen',
					},
					hintSettings: {
						mode: HintMode.Letters,
						totalHints: 3,
					},
				};
				wordGuessDemo.selectedInterfaceId =
					PluginInterfaceId.Interface1;

				editorGameDefinition.gamePreview = wordGuessDemo;
				editorGameDefinition.tags = [
					GameTag.Educational,
					GameTag.Component,
					GameTag.Entertainment,
					GameTag.FamiliarGames,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;
			}

			case PluginType.MiniCrypto: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-mini-crypto-editor/plugin-mini-crypto-editor.component'
					).then((m) => m.PluginMiniCryptoEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Beta;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.thumbnailClass = 'bi bi-question-square';
				editorGameDefinition.cardBackgroundColor = '#6B8BB8';

				// Create demo game with 3 levels
				const miniCryptoDemo = new PluginMiniCryptoGameData();
				miniCryptoDemo.title = 'Eläinkimara';
				miniCryptoDemo.description =
					'Täytä kaikki kirjainkentät. Sama numero = sama kirjain!';
				miniCryptoDemo.totalHints = 2;
				miniCryptoDemo.hintsRemaining = 2;
				miniCryptoDemo.selectedInterfaceId =
					PluginInterfaceId.Interface1;
				miniCryptoDemo.levels = [
					{
						words: [
							{
								word: 'KISSA',
								mapping: [1, 2, 3, 3, 4],
								hint: 'Pörröinen lemmikki',
							},
							{ word: 'KOIRA', mapping: [1, 5, 2, 6, 4] },
							{
								word: 'HEVONEN',
								mapping: [7, 8, 9, 5, 10, 8, 10],
							},
						],
					},
					{
						words: [
							{
								word: 'APINA',
								mapping: [1, 2, 3, 4, 1],
								hint: 'Viidakon eläin',
							},
							{ word: 'KARHU', mapping: [5, 1, 6, 7, 8] },
							{ word: 'SUSI', mapping: [9, 8, 9, 3] },
							{
								word: 'KROKOTIILI',
								mapping: [5, 6, 10, 5, 10, 11, 3, 3, 12, 3],
							},
						],
					},
					{
						words: [
							{
								word: 'LEPAKKO',
								mapping: [1, 2, 3, 4, 5, 5, 6],
								hint: 'Lentävä nisäkäs',
							},
							{
								word: 'KILPIKONNA',
								mapping: [5, 7, 1, 3, 7, 5, 6, 8, 8, 4],
							},
							{
								word: 'VIRTAHEPO',
								mapping: [9, 7, 10, 11, 4, 12, 2, 3, 6],
							},
							{ word: 'SEEPRA', mapping: [13, 2, 2, 3, 10, 4] },
							{
								word: 'STRUTSI',
								mapping: [13, 11, 10, 14, 11, 13, 7],
							},
						],
					},
				];

				editorGameDefinition.gamePreview = miniCryptoDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.Entertainment,
					GameTag.FamiliarGames,
					GameTag.Educational,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;
			}

			case PluginType.WordMaze: {
				editorGameDefinition.editorComponent = () =>
					import(
						'../plugin-word-maze-editor/plugin-word-maze-editor.component'
					).then((m) => m.PluginWordMazeEditorComponent);
				editorGameDefinition.displayMode = 'both';
				editorGameDefinition.defaultDisplayMode = 'modal';
				editorGameDefinition.state = GameProductionState.Beta;
				editorGameDefinition.supportAutoGeneration = true;
				editorGameDefinition.supportAutoGenerationWithManualSettings = true;
				editorGameDefinition.desktopSupport = true;
				editorGameDefinition.mobileSupport = true;
				editorGameDefinition.thumbnailClass = 'bi bi-grid-3x3';
				editorGameDefinition.cardBackgroundColor = '#B857A0';

				// Create demo game
				const wordMazeDemo = new PluginWordMazeGameData();
				wordMazeDemo.title = 'Eläinsokkelo';
				wordMazeDemo.description =
					'Bongaatko kaikki eläimet sanasokkelon uumenista?';
				wordMazeDemo.gridSize = 12;

				// Demo words - animals in Finnish
				const demoWords = [
					'KOIRA',
					'KISSA',
					'RUSAKKO',
					'LEIJONA',
					'KROKOTIILI',
					'GEPARDI',
					'JOUTSEN',
					'KETTU',
				];

				// Generate proper grid with word placement
				const { grid, wordPositions } =
					this.wordMazeGridGenerator.generateGrid(demoWords, 12);

				wordMazeDemo.grid = grid;
				wordMazeDemo.words = wordPositions;
				wordMazeDemo.selectedInterfaceId = PluginInterfaceId.Interface1;

				editorGameDefinition.gamePreview = wordMazeDemo;
				editorGameDefinition.tags = [
					GameTag.Component,
					GameTag.Entertainment,
					GameTag.FamiliarGames,
					GameTag.Educational,
				];
				editorGameDefinition.categories = [GameCategory.EasyGames];
				break;
			}
		}
	}
}
